<template>
  <!-- Global "New +" button -->
  <div class="app-menu__new-btn-wrapper">
    <Button
      :label="$t('sidebar.newButton')"
      icon="pi pi-plus"
      class="app-menu__new-btn bg-[linear-gradient(135deg,#2b3990,#2699d5)] hover:opacity-90 active:opacity-95 transition-opacity duration-200 border-none"
      pt:label:class="font-bold"
      raised
      @click="toggleNewMenu"
    />
    <Popover ref="newMenuRef">
      <div class="app-menu__new-popover">
        <div
          v-for="item in newMenuItems"
          :key="item.label || 'sep'"
          :class="item.separator ? 'app-menu__new-sep' : 'app-menu__new-item'"
          @click="!item.separator && (item.command(), newMenuRef.hide())"
        >
          <template v-if="!item.separator">
            <i :class="item.icon" />
            <span>{{ item.label }}</span>
          </template>
        </div>
      </div>
    </Popover>
  </div>

  <ul class="layout-menu app-menu">
    <template v-for="(item, i) in model" :key="item">
      <app-menu-item
        v-if="!item.separator"
        :item="item"
        :index="i"
      ></app-menu-item>
      <li v-if="item.separator" class="menu-separator"></li>
    </template>
  </ul>

  <div v-if="isFileManagerActive" class="folder-tree-section">
    <div
      class="folder-tree-header d-flex align-items-center justify-content-between"
    >
      <h6 class="mb-0">{{ $t("views.fileManager.folderStructure") }}</h6>
      <Button
        icon="pi pi-plus"
        size="small"
        severity="secondary"
        variant="text"
        :title="$t('views.fileManager.createFolderInMain')"
        @click="createFolderInMain"
      />
    </div>

    <div
      class="folder-item main-folder d-flex align-items-center gap-2 p-2 mb-1 mt-2"
      :class="{
        active: fileManagerStore.currentFolderId === null,
        'drop-target': dropTargetId === null
      }"
      @click="navigateToMainFolder"
      @dragover.prevent="handleDragOver(null)"
      @dragleave="handleDragLeave"
      @drop="handleDrop($event, null)"
    >
      <i class="pi pi-home text-primary"></i>
      <span class="grow">{{ $t("views.fileManager.mainFolder") }}</span>
    </div>

    <FolderTreeNode
      v-for="folder in fileManagerStore.folderTree"
      :key="folder.id"
      :folder="folder"
      :level="0"
      :current-folder-id="fileManagerStore.currentFolderId"
      :expanded-folders="fileManagerStore.expandedFolders"
      :drop-target-id="dropTargetId"
      @navigate="navigateToFolder"
      @expand="toggleFolderExpansion"
      @create-subfolder="createSubfolder"
      @delete-folder="deleteFolder"
      @folder-dragover="handleFolderDragOver"
      @folder-dragleave="handleFolderDragLeave"
      @folder-drop="handleFolderDrop"
    />

    <CreateFolderDialog
      v-model:visible="showCreateFolderDialog"
      :header-title="createFolderTitle"
      :is-loading="isCreatingFolder"
      @create="confirmCreateFolder"
      @cancel="cancelCreateFolder"
    />

    <ConfirmationDialog
      v-model="showDeleteDialog"
      :message="$t('views.fileManager.deleteConfirmation')"
      :loading="isDeleting"
      @confirm="confirmDeleteFolder"
    />
  </div>

  <!-- Global dialogs from "New +" menu -->
  <DocumentUploadDialog v-model:visible="showUploadDialog" />
  <NomenclatureItemDialog
    v-if="showNomenclatureDialog"
    v-model:visible="showNomenclatureDialog"
  />
</template>

<script setup>
import { computed, inject, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter, useRoute } from "vue-router"
import { useFileManagerStore } from "@/store"
import { useToast } from "primevue/usetoast"
import AppMenuItem from "./AppMenuItem.vue"
import FolderTreeNode from "@/components/fileManager/FolderTreeNode.vue"
import CreateFolderDialog from "@/components/fileManager/CreateFolderDialog.vue"
import ConfirmationDialog from "@/components/common/ConfirmationDialog.vue"
import DocumentUploadDialog from "@/components/documents/manage/DocumentUploadDialog.vue"
import NomenclatureItemDialog from "@/components/nomenclatures/NomenclatureItemDialog.vue"

const permissionsService = inject("permissionsService")
const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const fileManagerStore = useFileManagerStore()
const toast = useToast()

// ─── Global "New +" menu ──────────────────────────────────────────────────
const newMenuRef = ref(null)
const showUploadDialog = ref(false)
const showNomenclatureDialog = ref(false)

const toggleNewMenu = (event) => {
  newMenuRef.value.toggle(event)
}

const newMenuItems = computed(() => [
  {
    label: t("sidebar.newMenu.uploadDocuments"),
    icon: "pi pi-upload",
    command: () => {
      showUploadDialog.value = true
    }
  },
  {
    label: t("sidebar.newMenu.newFolder"),
    icon: "pi pi-folder-plus",
    command: () => {
      targetParentFolderId.value = null
      showCreateFolderDialog.value = true
    }
  },
  { separator: true },
  {
    label: t("sidebar.newMenu.newDocumentType"),
    icon: "pi pi-file-edit",
    command: () => {
      router.push({ name: "document-type-create" })
    }
  },
  {
    label: t("sidebar.newMenu.newNomenclature"),
    icon: "pi pi-tag",
    command: () => {
      showNomenclatureDialog.value = true
    }
  }
])

const dropTargetId = ref(undefined)
const showCreateFolderDialog = ref(false)
const showDeleteDialog = ref(false)
const isCreatingFolder = ref(false)
const isDeleting = ref(false)
const targetParentFolderId = ref(null)
const folderToDelete = ref(null)

const isFileManagerActive = computed(() => {
  return (
    route.path === "/file-manager" || route.path.startsWith("/file-manager")
  )
})

const createFolderTitle = computed(() => {
  if (targetParentFolderId.value === null) {
    return t("views.fileManager.createFolderInMain")
  }
  const parentFolder = fileManagerStore.allItems.find(
    (item) => item.id === targetParentFolderId.value
  )
  return t("views.fileManager.createFolderIn", {
    name: parentFolder?.name || ""
  })
})

const model = computed(() => {
  return filterMenuByPermissions([
    {
      label: t("sidebar.general.title"),
      items: [
        {
          label: t("sidebar.general.children.dashboard.title"),
          icon: "pi pi-fw pi-chart-line",
          to: "/"
        }
      ]
    },
    {
      label: t("sidebar.documents.title"),
      items: [
        {
          label: t("sidebar.documents.children.fileManager.title"),
          icon: "pi pi-fw pi-folder-open",
          to: "/file-manager"
        },
        {
          label: t("sidebar.documents.children.manageDocuments.title"),
          icon: "pi pi-fw pi-pen-to-square",
          to: "/manage-documents"
        }
      ]
    },
    {
      label: t("sidebar.settings.title"),
      items: [
        {
          label: t("sidebar.documents.children.connectors.title"),
          icon: "pi pi-fw pi-th-large",
          to: "/connectors"
        },
        {
          label: t("sidebar.documents.children.documentTypes.title"),
          icon: "pi pi-fw pi-file",
          to: "/document-types",
          permission: "platform.systems.document_types"
        },
        {
          label: t("sidebar.documents.children.nomenclatures.title"),
          icon: "pi pi-fw pi-tags",
          to: "/nomenclatures"
        }
      ]
    }
  ])
})

function filterMenuByPermissions(menu) {
  return menu
    .map((section) => {
      const filteredItems = section.items.filter(
        (item) =>
          !item.permission || permissionsService.hasPermission(item.permission)
      )
      return {
        ...section,
        items: filteredItems
      }
    })
    .filter((section) => section.items.length > 0)
}

const navigateToMainFolder = () => {
  fileManagerStore.openFolder(null)
  if (route.path !== "/file-manager") {
    router.push("/file-manager")
  }
}

const navigateToFolder = (folderId) => {
  fileManagerStore.openFolder(folderId)
  if (route.path !== "/file-manager") {
    router.push("/file-manager")
  }
}

const toggleFolderExpansion = (folderId) => {
  fileManagerStore.toggleFolderExpansion(folderId)
}

const createFolderInMain = () => {
  targetParentFolderId.value = null
  showCreateFolderDialog.value = true
}

const createSubfolder = (parentFolderId) => {
  targetParentFolderId.value = parentFolderId
  showCreateFolderDialog.value = true
}

const deleteFolder = (folderId) => {
  folderToDelete.value = folderId
  showDeleteDialog.value = true
}

const confirmDeleteFolder = async () => {
  try {
    isDeleting.value = true

    const currentFolderId = fileManagerStore.currentFolderId
    const isCurrentFolderDeleted = folderToDelete.value === currentFolderId

    await fileManagerStore.deleteItems([folderToDelete.value])

    if (isCurrentFolderDeleted) {
      fileManagerStore.resetPagination()
      fileManagerStore.currentFolderId = null
      await fileManagerStore.loadAllFolders()
      await fileManagerStore.fetchItems(null)
      fileManagerStore.expandAllFolders()

      toast.add({
        severity: "info",
        summary: t("common.info"),
        detail: t("views.fileManager.toasts.redirectedToMainFolder"),
        life: 5000
      })
    } else {
      await fileManagerStore.loadAllFolders()
      await fileManagerStore.fetchItems(fileManagerStore.currentFolderId)
      fileManagerStore.expandAllFolders()
    }

    showDeleteDialog.value = false
    toast.add({
      severity: "success",
      summary: t("common.success"),
      detail: t("views.fileManager.folderDeleted"),
      life: 3000
    })
  } catch (error) {
    toast.add({
      severity: "error",
      summary: t("common.error"),
      detail: error.message || t("views.fileManager.folderDeleteError"),
      life: 3000
    })
  } finally {
    isDeleting.value = false
    folderToDelete.value = null
  }
}

const handleDragOver = (folderId) => {
  dropTargetId.value = folderId
}

const handleDragLeave = () => {
  dropTargetId.value = undefined
}

const handleDrop = async (event, targetFolderId) => {
  event.preventDefault()
  dropTargetId.value = undefined

  let draggedItemId = event.dataTransfer.getData(
    "application/x-filemanager-item"
  )
  if (!draggedItemId) {
    draggedItemId = event.dataTransfer.getData("text/plain")
  }

  if (!draggedItemId) {
    return
  }

  let draggedItem = fileManagerStore.allItems.find(
    (item) =>
      item.id == draggedItemId ||
      item.id === String(draggedItemId) ||
      item.id === Number(draggedItemId)
  )

  if (!draggedItem) {
    draggedItem = fileManagerStore.currentItems.find(
      (item) =>
        item.id == draggedItemId ||
        item.id === String(draggedItemId) ||
        item.id === Number(draggedItemId)
    )
  }

  if (!draggedItem) {
    toast.add({
      severity: "error",
      summary: t("common.error"),
      detail: t("views.fileManager.toasts.itemNotFound"),
      life: 3000
    })
    return
  }

  if (draggedItem.id === targetFolderId) {
    return
  }

  const isDraggedItemSelected = fileManagerStore.selectedItems.some(
    (item) =>
      item.id == draggedItemId ||
      item.id === String(draggedItemId) ||
      item.id === Number(draggedItemId)
  )

  let itemsToMove = []
  let moveDescription = ""

  if (isDraggedItemSelected && fileManagerStore.selectedItems.length > 1) {
    itemsToMove = fileManagerStore.selectedItems.map((item) => item.id)
    const count = fileManagerStore.selectedItems.length
    const targetFolderName = targetFolderId
      ? fileManagerStore.allItems.find((item) => item.id === targetFolderId)
          ?.name || t("views.fileManager.toasts.unknownFolder")
      : t("views.fileManager.mainFolder")
    moveDescription = t("views.fileManager.toasts.moveMultipleItems", {
      count,
      targetFolderName
    })
  } else {
    itemsToMove = [draggedItemId]
    const targetFolderName = targetFolderId
      ? fileManagerStore.allItems.find((item) => item.id === targetFolderId)
          ?.name || t("views.fileManager.toasts.unknownFolder")
      : t("views.fileManager.mainFolder")
    moveDescription = t("views.fileManager.toasts.moveSingleItem", {
      itemName: draggedItem.name,
      targetFolderName
    })
  }

  try {
    await fileManagerStore.moveItems(itemsToMove, targetFolderId)

    if (isDraggedItemSelected && fileManagerStore.selectedItems.length > 1) {
      fileManagerStore.clearSelection()
    }

    await fileManagerStore.loadAllFolders()
    await fileManagerStore.fetchItems(fileManagerStore.currentFolderId)
    fileManagerStore.expandAllFolders()

    toast.add({
      severity: "success",
      summary: t("common.success"),
      detail: moveDescription,
      life: 4000
    })
  } catch (error) {
    toast.add({
      severity: "error",
      summary: t("common.error"),
      detail: error.message || t("views.fileManager.moveError"),
      life: 3000
    })
  }
}

const handleFolderDragOver = (event, folderId) => {
  event.preventDefault()
  dropTargetId.value = folderId
}

const handleFolderDragLeave = () => {
  dropTargetId.value = undefined
}

const handleFolderDrop = async (event, folderId) => {
  event.preventDefault()
  dropTargetId.value = undefined

  let draggedItemId = event.dataTransfer.getData(
    "application/x-filemanager-item"
  )
  if (!draggedItemId) {
    draggedItemId = event.dataTransfer.getData("text/plain")
  }

  if (!draggedItemId) {
    return
  }

  let draggedItem = fileManagerStore.allItems.find(
    (item) =>
      item.id == draggedItemId ||
      item.id === String(draggedItemId) ||
      item.id === Number(draggedItemId)
  )

  if (!draggedItem) {
    draggedItem = fileManagerStore.currentItems.find(
      (item) =>
        item.id == draggedItemId ||
        item.id === String(draggedItemId) ||
        item.id === Number(draggedItemId)
    )
  }

  if (!draggedItem) {
    toast.add({
      severity: "error",
      summary: t("common.error"),
      detail: t("views.fileManager.toasts.itemNotFound"),
      life: 3000
    })
    return
  }

  if (draggedItem.id === folderId) {
    return
  }

  const isDraggedItemSelected = fileManagerStore.selectedItems.some(
    (item) =>
      item.id == draggedItemId ||
      item.id === String(draggedItemId) ||
      item.id === Number(draggedItemId)
  )

  let itemsToMove = []
  let moveDescription = ""

  if (isDraggedItemSelected && fileManagerStore.selectedItems.length > 1) {
    itemsToMove = fileManagerStore.selectedItems.map((item) => item.id)
    const count = fileManagerStore.selectedItems.length
    const targetFolder = fileManagerStore.allItems.find(
      (item) => item.id === folderId
    )
    const targetFolderName =
      targetFolder?.name || t("views.fileManager.toasts.unknownFolder")
    moveDescription = t("views.fileManager.toasts.moveMultipleItems", {
      count,
      targetFolderName
    })
  } else {
    itemsToMove = [draggedItemId]
    const targetFolder = fileManagerStore.allItems.find(
      (item) => item.id === folderId
    )
    const targetFolderName =
      targetFolder?.name || t("views.fileManager.toasts.unknownFolder")
    moveDescription = t("views.fileManager.toasts.moveSingleItem", {
      itemName: draggedItem.name,
      targetFolderName
    })
  }

  try {
    await fileManagerStore.moveItems(itemsToMove, folderId)

    if (isDraggedItemSelected && fileManagerStore.selectedItems.length > 1) {
      fileManagerStore.clearSelection()
    }

    await fileManagerStore.loadAllFolders()
    await fileManagerStore.fetchItems(fileManagerStore.currentFolderId)
    fileManagerStore.expandAllFolders()

    toast.add({
      severity: "success",
      summary: t("common.success"),
      detail: moveDescription,
      life: 4000
    })
  } catch (error) {
    toast.add({
      severity: "error",
      summary: t("common.error"),
      detail: error.message || t("views.fileManager.moveError"),
      life: 3000
    })
  }
}

const confirmCreateFolder = async (folderName) => {
  if (!folderName.trim()) {
    toast.add({
      severity: "warn",
      summary: t("common.error"),
      detail: t("common.nameRequired"),
      life: 3000
    })
    return
  }

  isCreatingFolder.value = true
  try {
    await fileManagerStore.createFolder(folderName, targetParentFolderId.value)

    await fileManagerStore.loadAllFolders()
    await fileManagerStore.fetchItems(fileManagerStore.currentFolderId)
    fileManagerStore.expandAllFolders()

    toast.add({
      severity: "success",
      summary: t("common.success"),
      detail: t("views.fileManager.folderCreated"),
      life: 3000
    })
    showCreateFolderDialog.value = false
    targetParentFolderId.value = null
  } catch (error) {
    toast.add({
      severity: "error",
      summary: t("common.error"),
      detail: error.message || t("views.fileManager.folderCreateError"),
      life: 3000
    })
  } finally {
    isCreatingFolder.value = false
  }
}

const cancelCreateFolder = () => {
  showCreateFolderDialog.value = false
  targetParentFolderId.value = null
}

onMounted(async () => {
  if (!fileManagerStore.allItems || fileManagerStore.allItems.length === 0) {
    await fileManagerStore.loadAllFolders()
  }

  fileManagerStore.expandAllFolders()
})
</script>
